import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Workflow, Trash2, Edit2, Users, Layers, X, CheckCircle, FileText, FolderOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProjectIcon from '../components/ProjectIcon';
import ProjectMultiSelect from '../components/ProjectMultiSelect';
import HelperNote from '../components/HelperNote';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Workflows() {
  const { user } = useContext(AuthContext);
  const [workflows, setWorkflows] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [projects, setProjects] = useState([]);
  // Genişletilmiş workflow kartlarının id'leri (tüm adımları göster)
  const [expandedWorkflowIds, setExpandedWorkflowIds] = useState(new Set());
  const toggleWorkflowExpanded = (id) => {
    setExpandedWorkflowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    steps: [],
    projectIds: []
  });

  // Reset form to initial state
  const resetWorkflowForm = () => {
    setFormData({
      name: '',
      description: '',
      steps: [],
      projectIds: []
    });
    setCurrentStep({
      stepName: '',
      assignToType: 'user',
      assignTo: '',
      requiresApproval: false,
      stepType: 'Üretim'
    });
  };
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    steps: [],
    projectIds: []
  });
  const [currentStep, setCurrentStep] = useState({
    stepName: '',
    assignToType: 'user',
    assignToId: '',
    stepType: 'Üretim',
    tagId: '',
    quantityType: 'adet',
    quantityValue: '0'
  });
  const [editCurrentStep, setEditCurrentStep] = useState({
    stepName: '',
    assignToType: 'user',
    assignToId: '',
    stepType: 'Üretim',
    tagId: '',
    quantityType: 'adet',
    quantityValue: '0'
  });
  const [editingStepIndex, setEditingStepIndex] = useState(null);

  const fetchWorkflows = async (bypassCache = false) => {
    try {
      // ✅ Cache bypass için timestamp ekle
      const url = bypassCache 
        ? `${API}/workflows?_t=${Date.now()}`
        : `${API}/workflows`;
      
      const response = await axios.get(url, {
        headers: {
          'Cache-Control': bypassCache ? 'no-cache, no-store, must-revalidate' : 'default',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`📥 [WORKFLOWS] Fetched ${response.data.length} workflows (cache bypass: ${bypassCache})`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setWorkflows(sorted);
    } catch (error) {
      console.error('İş akışları yüklenemedi:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  useEffect(() => {
    // ⚡ PERFORMANCE: Paralel API çağrıları
    Promise.all([
      fetchWorkflows(),
      fetchUsers(),
      fetchTags(),
      fetchProjects()
    ]).catch(error => {
      console.error('❌ Veri yükleme hatası:', error);
    });
  }, []);

  const addStep = () => {
    if (!currentStep.stepName || !currentStep.assignToId || !currentStep.tagId) {
      toast.error('Lütfen tüm zorunlu alanları doldurun (Adım Adı, Kullanıcı, Kategori)');
      return;
    }
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { ...currentStep, order: prev.steps.length }]
    }));
    setCurrentStep({ stepName: '', assignToType: 'user', assignToId: '', stepType: 'Üretim', tagId: '', quantityType: 'adet', quantityValue: '0' });
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i }))
    }));
  };

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    if (formData.steps.length === 0) {
      toast.error('En az bir adım eklemelisiniz');
      return;
    }
    
    console.log('📤 [WORKFLOW CREATE] Sending data:', formData);
    
    try {
      const response = await axios.post(`${API}/workflows`, formData, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json'
        },
        // ✅ Axios'a başarılı response'ları kabul et
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      });
      
      console.log('✅ [WORKFLOW CREATE] Success:', response.status, response.data);
      
      toast.success('İş akışı oluşturuldu');
      setShowCreateDialog(false);
      resetWorkflowForm();
      
      // ✅ PWA için hard reload - cache bypass
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
      }, 800);
      
    } catch (error) {
      console.error('❌ [WORKFLOW CREATE] Error:', error);
      console.error('   Response status:', error.response?.status);
      console.error('   Response data:', error.response?.data);
      console.error('   Error code:', error.code);
      
      // Gerçek hata durumları
      if (error.response?.status === 403) {
        toast.error('İş akışı oluşturma yetkiniz yok');
      } else if (error.response?.status === 500) {
        toast.error('Sunucu hatası: ' + (error.response?.data?.detail || 'Beklenmeyen hata'));
        // Backend'de başarılı olmuş olabilir, kontrol için yenile
        setTimeout(() => {
          window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
        }, 2000);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error('İstek zaman aşımına uğradı');
        // Timeout olsa bile backend işlemi tamamlamış olabilir
        setTimeout(() => {
          window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
        }, 2000);
      } else if (error.response?.data?.detail) {
        toast.error('İş akışı oluşturulamadı: ' + error.response.data.detail);
      } else {
        toast.error('İş akışı oluşturulamadı: ' + (error.message || 'Bilinmeyen hata'));
      }
    }
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.name : 'Bilinmeyen kullanıcı';
  };

  const handleEditWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setEditFormData({
      name: workflow.name,
      description: workflow.description || '',
      steps: workflow.steps || [],
      projectIds: workflow.projectIds || []
    });
    setEditingStepIndex(null);
    setEditCurrentStep({ stepName: '', assignToType: 'user', assignToId: '' });
    setShowEditDialog(true);
  };

  const addEditStep = () => {
    if (!editCurrentStep.stepName || !editCurrentStep.assignToId || !editCurrentStep.tagId) {
      toast.error('Lütfen tüm zorunlu alanları doldurun (Adım Adı, Kullanıcı, Kategori)');
      return;
    }
    setEditFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { ...editCurrentStep, order: prev.steps.length }]
    }));
    setEditCurrentStep({ 
      stepName: '', 
      assignToType: 'user', 
      assignToId: '',
      stepType: 'Üretim',
      tagId: '',
      quantityType: 'adet',
      quantityValue: '0'
    });
  };

  const startEditingStep = (index) => {
    const step = editFormData.steps[index];
    setEditCurrentStep({
      stepName: step.stepName,
      assignToType: 'user',
      assignToId: step.assignToId,
      stepType: step.stepType || 'Üretim',
      tagId: step.tagId || '',
      quantityType: step.quantityType || 'adet',
      quantityValue: step.quantityValue || '0'
    });
    setEditingStepIndex(index);
  };

  const saveEditingStep = () => {
    if (!editCurrentStep.stepName || !editCurrentStep.assignToId || !editCurrentStep.tagId) {
      toast.error('Adım adı, kullanıcı ve kategori zorunludur');
      return;
    }
    
    setEditFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === editingStepIndex 
          ? { ...editCurrentStep, order: i }
          : step
      )
    }));
    
    setEditingStepIndex(null);
    setEditCurrentStep({ 
      stepName: '', 
      assignToType: 'user', 
      assignToId: '',
      stepType: 'Üretim',
      tagId: '',
      quantityType: 'adet',
      quantityValue: '0'
    });
    toast.success('Adım güncellendi');
  };

  const cancelEditingStep = () => {
    setEditingStepIndex(null);
    setEditCurrentStep({ 
      stepName: '', 
      assignToType: 'user', 
      assignToId: '',
      stepType: 'Üretim',
      tagId: '',
      quantityType: 'adet',
      quantityValue: '0'
    });
  };

  const removeEditStep = (index) => {
    setEditFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i }))
    }));
  };

  const handleUpdateWorkflow = async (e) => {
    e.preventDefault();
    if (editFormData.steps.length === 0) {
      toast.error('En az bir adım eklemelisiniz');
      return;
    }
    try {
      await axios.patch(`${API}/workflows/${selectedWorkflow.id}`, editFormData);
      toast.success('İş akışı güncellendi');
      setShowEditDialog(false);
      fetchWorkflows(true);
    } catch (error) {
      toast.error('İş akışı güncellenemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleDeleteWorkflow = async (workflow) => {
    if (!window.confirm(`"${workflow.name}" iş akışını silmek istediğinizden emin misiniz?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/workflows/${workflow.id}`);
      toast.success('İş akışı silindi');
      
      // ✅ PWA için hard reload
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
      }, 800);
    } catch (error) {
      toast.error('İş akışı silinemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const canManageWorkflows = user?.permissions?.includes('can_manage_workflows');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6" data-testid="workflows-page">
      {/* Header */}
      <div className="flex items-center justify-between pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Workflow className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              İş Akışları
              {/* İş akışı sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {workflows.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Görev otomasyonu için iş akışları oluşturun ve yönetin
            {workflows.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {workflows.length.toLocaleString('tr-TR')} iş akışı</span>
            )}
          </p>
        </div>
        
        {canManageWorkflows && (
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) resetWorkflowForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700" data-testid="create-workflow-button">
                <Plus className="w-4 h-4 mr-2" />
                Yeni İş Akışı
              </Button>
            </DialogTrigger>
            
            <DialogContent className="w-[95vw] sm:w-[90vw] lg:w-full max-w-3xl max-h-[90vh] overflow-y-auto !p-0 !gap-0">
              {/* Close Button */}
              <button
                onClick={() => setShowCreateDialog(false)}
                className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
              >
                <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
              </button>
              
              <DialogHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 px-4 sm:px-6 py-4 sm:py-5 rounded-t-lg">
                <DialogTitle className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Workflow className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  Yeni İş Akışı Oluştur
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleCreateWorkflow} className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                {/* Workflow Info */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-3 sm:p-5 rounded-xl border-2 border-cyan-200">
                    <Label htmlFor="name" className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                      <Workflow className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
                      İş Akışı Adı <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Örn: Sosyal Medya İçerik Üretimi"
                      required
                      className="bg-white border-2 border-cyan-300 focus:border-cyan-500 focus:ring-cyan-500"
                      data-testid="workflow-name-input"
                    />
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-5 rounded-xl border-2 border-purple-200">
                    <Label htmlFor="description" className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                      Açıklama
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="İş akışının amacını kısaca açıklayın..."
                      rows={3}
                      className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                      data-testid="workflow-description-input"
                    />
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-3 sm:p-5 rounded-xl border-2 border-amber-200">
                    <Label className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                      <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                      Bağlı Projeler (opsiyonel)
                    </Label>
                    <ProjectMultiSelect
                      projects={projects}
                      selectedIds={formData.projectIds}
                      onChange={(ids) => setFormData({ ...formData, projectIds: ids })}
                      testIdPrefix="workflow-create-projects"
                    />
                    <HelperNote variant="info" className="mt-2">
                      Bu iş akışı yalnızca seçtiğiniz projelerdeki görev oluşturma listesinde görünür. Hiç proje seçilmezse hiçbir listede görünmez.
                    </HelperNote>
                  </div>
                </div>

                {/* Steps Section */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">İş Akışı Adımları</h3>
                    <span className="text-xs sm:text-sm text-gray-600">{formData.steps.length} adım</span>
                  </div>

                  {/* Existing Steps */}
                  {formData.steps.length > 0 && (
                    <div className="space-y-2 sm:space-y-3">
                      {formData.steps.map((step, index) => (
                        <div key={index} className="flex items-start sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-2 border-blue-200">
                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-md flex items-center justify-center text-white text-sm sm:text-base font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                              <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{step.stepName}</p>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${
                                step.stepType === 'Kontrol' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {step.stepType || 'Üretim'}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              <span className="truncate max-w-[120px] sm:max-w-none">{getUserName(step.assignToId)}</span>
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStep(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Step */}
                  <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-white space-y-4">
                    <h4 className="font-semibold text-gray-900">Yeni Adım Ekle</h4>
                    
                    <div>
                      <Label className="text-sm font-medium">Adım Adı *</Label>
                      <Input
                        value={currentStep.stepName}
                        onChange={(e) => setCurrentStep({ ...currentStep, stepName: e.target.value })}
                        placeholder="Örn: İçerik Yazımı, Grafik Tasarım, Son Kontrol..."
                        className="mt-1"
                        data-testid="step-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Adım Tipi *</Label>
                      <Select 
                        value={currentStep.stepType} 
                        onValueChange={(value) => setCurrentStep({ ...currentStep, stepType: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="step-type-select">
                          <SelectValue placeholder="Adım tipini seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Üretim">Üretim</SelectItem>
                          <SelectItem value="Kontrol">Kontrol</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Kategori *</Label>
                      <Select 
                        value={currentStep.tagId} 
                        onValueChange={(value) => setCurrentStep({ ...currentStep, tagId: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Kategori seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {tags.map((tag) => (
                            <SelectItem key={tag.id} value={tag.id}>
                              {tag.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Miktar Tipi *</Label>
                        <Select 
                          value={currentStep.quantityType} 
                          onValueChange={(value) => {
                            const defaultValue = value === 'adet' ? '0' : '00:00:00';
                            setCurrentStep({ ...currentStep, quantityType: value, quantityValue: defaultValue });
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Tip seçin..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="adet">Adet</SelectItem>
                            <SelectItem value="süre">Süre (HH:MM:SS)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Varsayılan Değer</Label>
                        <Input
                          value={currentStep.quantityValue}
                          onChange={(e) => setCurrentStep({ ...currentStep, quantityValue: e.target.value })}
                          placeholder={currentStep.quantityType === 'adet' ? '0' : '00:00:00'}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Sorumlu Kullanıcı *</Label>
                      <Select 
                        value={currentStep.assignToId} 
                        onValueChange={(value) => setCurrentStep({ ...currentStep, assignToId: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="assign-user-select">
                          <SelectValue placeholder="Kullanıcı seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                                  <span className="text-xs font-semibold text-blue-600">
                                    {user.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                {user.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={addStep} 
                      className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 h-10"
                      data-testid="add-step-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adım Ekle
                    </Button>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t-2 border-gray-200 flex flex-col sm:flex-row gap-2 sm:gap-3 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 mt-4 sm:mt-6">
                  <Button 
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => setShowCreateDialog(false)}
                    className="flex-1 border-2 hover:bg-gray-100 min-h-[44px] py-3"
                  >
                    <X className="w-4 h-4 mr-2" />
                    İptal
                  </Button>
                  <Button 
                    type="submit"
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg min-h-[44px] py-3" 
                    data-testid="submit-workflow-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    İş Akışını Oluştur
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Workflows Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {workflows.map((workflow) => (
          <div 
            key={workflow.id} 
            className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-blue-200" 
            data-testid={`workflow-card-${workflow.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Workflow className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2">{workflow.name}</h3>
                  {workflow.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{workflow.description}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                {canManageWorkflows && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditWorkflow(workflow)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`edit-workflow-${workflow.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteWorkflow(workflow)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-workflow-${workflow.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Project Icons Row - Below header so they don't crowd the title */}
            {workflow.projectIds && workflow.projectIds.length > 0 && (() => {
              const linked = workflow.projectIds
                .map(pid => projects.find(p => p.id === pid))
                .filter(Boolean);
              if (!linked.length) return null;
              return (
                <div className="flex items-center gap-1.5 flex-wrap justify-end mb-1" data-testid={`workflow-project-icons-${workflow.id}`}>
                  {linked.map(proj => (
                    <ProjectIcon
                      key={proj.id}
                      project={proj}
                      size="md"
                      className="ring-2 ring-white"
                    />
                  ))}
                </div>
              );
            })()}

            {/* Workflow Info */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <Layers className="w-4 h-4 text-blue-600" />
                <span className="font-medium">{workflow.steps.length} Adım</span>
              </div>
              
              {/* Steps List */}
              {workflow.steps.length > 0 ? (
                <div className="space-y-1">
                  {(expandedWorkflowIds.has(workflow.id)
                    ? workflow.steps
                    : workflow.steps.slice(0, 5)
                  ).map((step, index) => {
                    const assignedUser = users.find(u => u.id === step.assignToId);
                    return (
                      <div key={index} className="flex items-center gap-2 text-xs text-gray-600 hover:bg-gray-50 rounded px-2 py-1.5">
                        <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></span>
                        <span className="flex-1 truncate font-medium">{step.stepName}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-md flex-shrink-0 ${
                          step.stepType === 'Kontrol' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {step.stepType || 'Üretim'}
                        </span>
                        {assignedUser && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md flex items-center gap-1 flex-shrink-0">
                            <Users className="w-3 h-3" />
                            {assignedUser.name}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {workflow.steps.length > 5 && (
                    <button
                      type="button"
                      onClick={() => toggleWorkflowExpanded(workflow.id)}
                      data-testid={`toggle-workflow-steps-${workflow.id}`}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium ml-3 mt-1 cursor-pointer transition-colors"
                    >
                      {expandedWorkflowIds.has(workflow.id)
                        ? 'Daha az göster'
                        : `+${workflow.steps.length - 5} adım daha`}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Henüz adım eklenmemiş</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {workflows.length === 0 && (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Workflow className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz iş akışı yok</h3>
          <p className="text-gray-600 mb-4">Görev otomasyonunu başlatmak için ilk iş akışınızı oluşturun</p>
          {canManageWorkflows && (
            <Button onClick={() => {
              resetWorkflowForm();
              setShowCreateDialog(true);
            }} className="bg-gradient-to-r from-cyan-600 to-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              İlk İş Akışını Oluştur
            </Button>
          )}
        </div>
      )}

      {/* Edit Workflow Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-[95vw] sm:w-[90vw] lg:w-full max-w-3xl max-h-[90vh] overflow-y-auto !p-0 !gap-0">
          {/* Close Button */}
          <button
            onClick={() => setShowEditDialog(false)}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          <DialogHeader className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 px-4 sm:px-6 py-4 sm:py-5 rounded-t-lg">
            <DialogTitle className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Edit2 className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              İş Akışını Düzenle
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateWorkflow} className="space-y-4 sm:space-y-6 p-4 sm:p-6">
            {/* Workflow Info */}
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 sm:p-5 rounded-xl border-2 border-blue-200">
                <Label htmlFor="edit-name" className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                  <Workflow className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  İş Akışı Adı <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  className="bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-5 rounded-xl border-2 border-purple-200">
                <Label htmlFor="edit-description" className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  Açıklama
                </Label>
                <Textarea
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                  className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-3 sm:p-5 rounded-xl border-2 border-amber-200">
                <Label className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2 sm:mb-3">
                  <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  Bağlı Projeler (opsiyonel)
                </Label>
                <ProjectMultiSelect
                  projects={projects}
                  selectedIds={editFormData.projectIds || []}
                  onChange={(ids) => setEditFormData({ ...editFormData, projectIds: ids })}
                  testIdPrefix="workflow-edit-projects"
                />
                <HelperNote variant="info" className="mt-2">
                  Bu iş akışı yalnızca seçtiğiniz projelerdeki görev oluşturma listesinde görünür. Hiç proje seçilmezse hiçbir listede görünmez.
                </HelperNote>
              </div>
            </div>

            {/* Steps Section */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">İş Akışı Adımları</h3>
                <span className="text-xs sm:text-sm text-gray-600">{editFormData.steps.length} adım</span>
              </div>

              {/* Existing Steps */}
              {editFormData.steps.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  {editFormData.steps.map((step, index) => (
                    <div key={index} className="flex items-start sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-2 border-blue-200">
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-md flex items-center justify-center text-white text-sm sm:text-base font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                          <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{step.stepName}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${
                            step.stepType === 'Kontrol' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {step.stepType || 'Üretim'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
                          <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span className="truncate max-w-[120px] sm:max-w-none">{getUserName(step.assignToId)}</span>
                          </p>
                          {step.tagId && (
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                              <FolderOpen className="w-3 h-3" />
                              <span className="truncate max-w-[100px] sm:max-w-none">{tags.find(t => t.id === step.tagId)?.name || 'Kategori'}</span>
                            </p>
                          )}
                          {step.quantityType && step.quantityValue && (
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1">
                              <Layers className="w-3 h-3" />
                              {step.quantityValue} {step.quantityType === 'adet' ? 'adet' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditingStep(index)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditStep(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Step Form */}
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-white space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">
                    {editingStepIndex !== null ? `${editingStepIndex + 1}. Adımı Düzenle` : 'Yeni Adım Ekle'}
                  </h4>
                  {editingStepIndex !== null && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={cancelEditingStep}
                      className="text-gray-600"
                    >
                      İptal
                    </Button>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Adım Adı *</Label>
                  <Input
                    value={editCurrentStep.stepName}
                    onChange={(e) => setEditCurrentStep({ ...editCurrentStep, stepName: e.target.value })}
                    placeholder="Örn: İçerik Yazımı, Grafik Tasarım..."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Adım Tipi *</Label>
                  <Select 
                    value={editCurrentStep.stepType || 'Üretim'} 
                    onValueChange={(value) => setEditCurrentStep({ ...editCurrentStep, stepType: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Adım tipini seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Üretim">Üretim</SelectItem>
                      <SelectItem value="Kontrol">Kontrol</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Kategori *</Label>
                  <Select 
                    value={editCurrentStep.tagId} 
                    onValueChange={(value) => setEditCurrentStep({ ...editCurrentStep, tagId: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Kategori seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Miktar Tipi *</Label>
                    <Select 
                      value={editCurrentStep.quantityType} 
                      onValueChange={(value) => {
                        const defaultValue = value === 'adet' ? '0' : '00:00:00';
                        setEditCurrentStep({ ...editCurrentStep, quantityType: value, quantityValue: defaultValue });
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Tip seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adet">Adet</SelectItem>
                        <SelectItem value="süre">Süre (HH:MM:SS)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Varsayılan Değer</Label>
                    <Input
                      value={editCurrentStep.quantityValue}
                      onChange={(e) => setEditCurrentStep({ ...editCurrentStep, quantityValue: e.target.value })}
                      placeholder={editCurrentStep.quantityType === 'adet' ? '0' : '00:00:00'}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Sorumlu Kullanıcı *</Label>
                  <Select 
                    value={editCurrentStep.assignToId} 
                    onValueChange={(value) => setEditCurrentStep({ ...editCurrentStep, assignToId: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Kullanıcı seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                              <span className="text-xs font-semibold text-blue-600">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            {user.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {editingStepIndex !== null ? (
                  <Button 
                    type="button" 
                    onClick={saveEditingStep} 
                    className="w-full bg-blue-600 hover:bg-blue-700 h-10"
                  >
                    Değişiklikleri Kaydet
                  </Button>
                ) : (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addEditStep} 
                    className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 h-10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adım Ekle
                  </Button>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t-2 border-gray-200 flex flex-col sm:flex-row gap-2 sm:gap-3 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 mt-4 sm:mt-6">
              <Button 
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 border-2 hover:bg-gray-100 min-h-[44px] py-3"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                type="submit"
                size="lg"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg min-h-[44px] py-3"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
